/**
 * Pick a custom report for a tile to show.
 *
 * The list is the SAME one `/reports` shows — `scope: 'all'`, so anything
 * shared with the author is placeable, not only what they wrote. Placing a
 * report they can reach but their audience cannot is not a leak: each viewer
 * resolves the tile under their own reach.
 */
import type { SavedReport } from '@clockwise/clients/report-definitions';
import { useQuery } from '@tanstack/react-query';
import { Table2 } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { reportDefinitionsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const QUERY = { scope: 'all', sortField: 'name', sortDirection: 'asc', limit: 200 } as const;

interface Props {
  /** The report the tile shows now, marked in the list. */
  currentId?: string;
  onPick: (report: SavedReport) => void;
}

export function CustomReportPicker({ currentId, onPick }: Props) {
  const reports = useQuery({
    queryKey: queryKeys.reportDefinitions.list(QUERY),
    queryFn: () => reportDefinitionsApi.list(QUERY),
  });

  return (
    <Command className="rounded-md border">
      <CommandInput placeholder="Search your custom reports…" autoFocus />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>
          {reports.isPending
            ? 'Loading…'
            : reports.isError
              ? 'Could not load your custom reports.'
              : 'No custom reports yet. Build one on the dashboard instead.'}
        </CommandEmpty>
        <CommandGroup>
          {(reports.data?.data ?? []).map((report) => (
            <CommandItem
              key={report.id}
              // The NAME, not the id: `Command` filters on this value, and
              // filtering a report list by uuid helps nobody.
              value={report.name}
              onSelect={() => onPick(report)}
            >
              <Table2 className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="truncate">{report.name}</span>
              <span className="ml-auto text-[10px] text-muted-foreground">
                {report.id === currentId ? 'current' : report.owned ? '' : 'shared'}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
